import * as React from "react";
import Link from "next/link";
import { useMachine } from "@xstate/react";
import { useOfferMode } from "@nju/data-access-portal-offers";
import { onboardingStarted } from "@nju/data-access-portal-orchestrator";
import { OrchestratorsContext } from "@nju/data-access-portal-orchestrator";
import { ClientOrderInfoBox } from "@nju/feature-sales-client";
import { Icon } from "@nju/icons";
import { salesRoutes } from "@nju/routes";
import {
  A,
  Badge,
  Button,
  GeneralErrorNotification,
  Heading,
  Separator,
  Text,
} from "@nju/ui";
import { copyToClipboard } from "@nju/utils";
import { placeOrderSuccessMachine } from "./placeOrderSuccess.machine";

export function PlaceOrderSuccessPage() {
  const [{ context, matches }] = useMachine(placeOrderSuccessMachine);
  const {
    ftthOrchestrator,
    multiOrchestrator,
    mobilesOrchestrators,
    isAddressInFtthRange,
  } = context;
  const mode = useOfferMode();
  const mobilesMultiOrderItem = mobilesOrchestrators?.map((item) => {
    return { orderId: item.orderId };
  });
  const multiOrderItems =
    multiOrchestrator?.multiOrderItems || mobilesMultiOrderItem;
  const ftthOrderId = ftthOrchestrator?.orderId;

  const handleCopyToClipboard = React.useCallback(async (text: string) => {
    await copyToClipboard(text);
  }, []);

  const showClientHasMVMessage =
    mode === "ftth" &&
    multiOrchestrator &&
    onboardingStarted(multiOrchestrator.state);

  if (matches("ERROR")) {
    return (
      <GeneralErrorNotification
        callback={() => {
          window.location.reload();
        }}
      />
    );
  }

  return (
    <OrchestratorsContext.Provider
      value={{
        multiOrder: multiOrchestrator,
      }}
    >
      <div>
        <div className="bg-white" data-cy="client-order-info-box">
          <ClientOrderInfoBox />
        </div>
        <div className="flex flex-col items-center h-full max-w-[660px] my-0 mx-auto pt-8 pb-8 px-3 from-tablet:px-5">
          <Heading
            align="center"
            font="ttfirs"
            tone="neutral"
            variant="h2"
            weight="bold"
          >
            zamówienie zostało złożone
          </Heading>
          <Separator size="s" tone="invisible" />
          <Icon
            className="w-12 h-12 from-tablet:w-[90px] from-tablet:h-[90px]"
            name="hand-incircle-success"
          />
          <Separator size="s" tone="invisible" />
          <>
            <Text
              align="center"
              size={{
                "@fromTablet": `keyS`,
              }}
              weight="bold"
            >
              Świetna robota!
            </Text>
            <Separator size="s" tone="invisible" />
          </>
          <Text
            align="center"
            size={{
              "@fromTablet": "m",
            }}
          >
            Klient otrzyma potwierdzenie zamówienia na swój adres mailowy.
          </Text>
          <Text
            align="center"
            size={{
              "@fromTablet": "m",
            }}
          >
            Poinformuj klienta o sposobie aktywacji.
          </Text>
          <Text
            align="center"
            size={{
              "@fromTablet": "m",
            }}
          >
            Dokumenty formalne wysłaliśmy na adres e-mail klienta.
          </Text>
          <Separator size="xs" tone="invisible" />
          {showClientHasMVMessage && (
            <Text
              align="center"
              size={{
                "@fromTablet": "xl",
              }}
            >
              Klient ma już zakupioną usługę nju subskrypcja.
            </Text>
          )}
          <>
            <Separator size="s" />
            <Text
              align="center"
              size={{
                "@fromTablet": "xl",
              }}
              weight="bold"
            >
              numer zamówienia:
            </Text>
            {mode === "ftth" && ftthOrderId && (
              <div className="flex" data-cy="ftth-dop-order-id">
                <Text
                  align="center"
                  className="pt-2"
                  size={{
                    "@fromTablet": "m",
                  }}
                  weight="bold"
                >
                  {ftthOrderId}
                </Text>
                <Button
                  display="inline"
                  onPress={() => {
                    handleCopyToClipboard(ftthOrderId as string);
                  }}
                  size="inline"
                  variant="simple"
                >
                  <Icon className="ml-2" name="copy" />
                </Button>
              </div>
            )}
            {mode === "mobile" &&
              multiOrderItems &&
              multiOrderItems.map((item) => {
                return (
                  <div
                    className="flex"
                    data-cy="mv-dop-order-id"
                    key={item.orderId}
                  >
                    <Text
                      align="center"
                      className="pt-2"
                      size={{
                        "@fromTablet": "m",
                      }}
                      weight="bold"
                    >
                      {item.orderId}
                    </Text>
                    <Button
                      display="inline"
                      onPress={() => {
                        handleCopyToClipboard(item.orderId as string);
                      }}
                      size="inline"
                      variant="simple"
                    >
                      <Icon className="ml-2" name="copy" />
                    </Button>
                  </div>
                );
              })}
            <Separator size="m" />
            <Text
              align="center"
              size={{
                "@fromTablet": "xl",
              }}
              weight="bold"
            >
              zaproponuj więcej
            </Text>
            {isAddressInFtthRange &&
              (!ftthOrchestrator || ftthOrchestrator?.state === "START") && (
                <>
                  <Separator size="xxs" tone="invisible" />
                  <Badge
                    badgeText="klient w zasięgu FTTH - zaproponuj usługę"
                    name="globe"
                  />
                </>
              )}
            <Separator size="xxs" tone="invisible" />
            <Text
              align="center"
              size={{
                "@fromTablet": "m",
              }}
            >
              Przejdź do panelu klienta i sprzedaj mu więcej.
            </Text>
            <Separator size="s" tone="invisible" />
          </>
          <div className="flex flex-col-reverse from-tablet:flex-row justify-between gap-3 from-tablet:gap-6 w-full from-tablet:w-auto">
            <Link href={salesRoutes.panel.get()} passHref={true}>
              <A
                size={{ "@onlyMobile": "s", "@fromTablet": "m" }}
                variant="secondary"
              >
                panel konsultanta
              </A>
            </Link>
            <Link href={salesRoutes.statusKlienta.get()} passHref={true}>
              <A size={{ "@onlyMobile": "s", "@fromTablet": "m" }}>
                panel klienta
              </A>
            </Link>
          </div>
        </div>
      </div>
    </OrchestratorsContext.Provider>
  );
}
